import { useState, useEffect, useMemo } from "react";
import {
  Shield,
  Search,
  SlidersHorizontal,
  Plus,
  Eye,
  Trash2,
  X,
  Pencil,
  UsersRound,
  Globe,
  Share2,
  CheckSquare,
  Square,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../../utils/toast";
import {
  listSafetyTemplates,
  deleteSafetyTemplate,
  getProjectsForCurrentUser,
  getSafetyTemplate,
} from "../../../api";
import SafetyReportTemplate from "./SafetyReportTemplate";
import HousekeepingReportTemplate from "../Housekeeping_setup/HousekeepingReportTemplate";
import ObservationReportTemplate from "./ObservationReportTemplate";
import SafetyReportTemplateHorizontal from "./SafetyReportTemplateHorizontal";
import SafetyProjectAssignmentModal from "./SafetyProjectAssignmentModal";
import SafetyProjectReportConfigModal from "./SafetyProjectReportConfigModal";

// Column config: add or remove columns here; table stays consistent
const COLUMNS = [
  { id: "select", label: "", width: "minmax(2.5rem, 0.4fr)", dataKey: null },
  { id: "sr_no", label: "SR NO.", width: "minmax(3.5rem, 0.6fr)", dataKey: null },
  {
    id: "format_no",
    label: "FORMAT NO.",
    width: "minmax(7rem, 1.2fr)",
    dataKey: "format_no",
  },
  {
    id: "format_name",
    label: "FORMAT NAME",
    width: "minmax(12rem, 2fr)",
    dataKey: "title",
  },
  {
    id: "status",
    label: "STATUS",
    width: "minmax(5.5rem, 0.9fr)",
    dataKey: "status",
  },
  {
    id: "category",
    label: "CATEGORY",
    width: "minmax(7.5rem, 1.1fr)",
    dataKey: "category_name",
  },
  {
    id: "project",
    label: "PROJECT / ORIGIN",
    width: "minmax(10.5rem, 1.7fr)",
    dataKey: "project_name",
  },
  {
    id: "actions",
    label: "ACTIONS",
    width: "minmax(9.5rem, 1.5fr)",
    dataKey: null,
  },
];

const gridTemplateColumns = COLUMNS.map((c) => c.width).join(" ");

function formatStatus(status) {
  if (!status) return "—";
  const s = String(status).toUpperCase();
  if (s === "ACTIVE") return "Active";
  if (s === "DEACTIVE") return "Deactive";
  if (s === "ARCHIVED") return "Archived";
  return status;
}

function getFormatNo(reportHeaderMeta) {
  if (!reportHeaderMeta || typeof reportHeaderMeta !== "object") return null;
  return (
    reportHeaderMeta.format_no ??
    reportHeaderMeta.format_no_ ??
    reportHeaderMeta["format_no"] ??
    reportHeaderMeta.formatNumber ??
    null
  );
}

function getCellValue(template, column, index, projectMap) {
  if (column.id === "select") return null;
  if (column.id === "sr_no") return index + 1;
  if (column.id === "actions") return null;

  if (column.id === "format_no") {
    const meta = template.report_header_meta;
    const no = getFormatNo(meta);
    return no != null && no !== ""
      ? String(no)
      : (template.template_code ?? template.id ?? "—");
  }

  if (column.id === "format_name") {
    return template.title ?? template.name ?? template.report_title ?? "—";
  }

  if (column.id === "status") {
    return formatStatus(template.status);
  }

  if (column.id === "category") {
    return template.category_name ?? template.category?.name ?? "—";
  }

  if (column.id === "project") {
    const pid = template.project_id;
    const name =
      projectMap?.get(pid) ?? template.project_name ?? template.project?.name;
    return name ?? (pid != null ? `Project #${pid}` : "—");
  }

  const key = column.dataKey;
  if (!key) return "—";
  const val = template[key];
  if (val == null || val === "") return "—";
  if (typeof val === "object" && val?.name) return val.name;
  return String(val);
}

function SafetyFormats() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Safety Checklist");
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  // Assignment Modal & Selection State
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(new Set());
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assignmentTargetTemplate, setAssignmentTargetTemplate] = useState(null);
  const [assignmentTargetTemplates, setAssignmentTargetTemplates] = useState([]);
  const [isProjectReportConfigModalOpen, setIsProjectReportConfigModalOpen] = useState(false);

  const projectMap = useMemo(() => {
    const m = new Map();
    (projects || []).forEach((p) => {
      const id = p.id ?? p.project_id;
      const name = p.name ?? p.project_name ?? p.project_title;
      if (id != null) m.set(Number(id), name ?? `Project #${id}`);
    });
    return m;
  }, [projects]);

  // Org ID for filtering templates: from user's projects (same as getProjectsForCurrentUser) or USER_DATA
  const orgId = useMemo(() => {
    const list = projects || [];
    if (list.length > 0) {
      const p = list[0];
      return p.organization_id ?? p.org_id ?? p.org ?? null;
    }
    try {
      const userStr = localStorage.getItem("USER_DATA");
      const user =
        userStr && userStr !== "undefined" ? JSON.parse(userStr) : null;
      return user?.org ?? user?.organization_id ?? null;
    } catch {
      return null;
    }
  }, [projects]);

  const fetchProjects = async () => {
    try {
      const res = await getProjectsForCurrentUser();
      const raw = res?.data ?? res;
      const list = Array.isArray(raw) ? raw : (raw?.results ?? []);
      setProjects(list);
    } catch (_e) {
      setProjects([]);
    }
  };

  const fetchTemplates = async () => {
    if (orgId == null) {
      setLoading(false);
      setTemplates([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await listSafetyTemplates({
        org_id: orgId,
        is_latest: true,
      });
      const data = res?.data ?? res;
      const list = Array.isArray(data) ? data : (data?.results ?? []);
      setTemplates(list);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to load templates.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [orgId]);

  const filteredTemplates = useMemo(() => {
    let result = templates;

    if (selectedCategory !== "All") {
      result = result.filter((t) => {
        const cat = (t.category_name ?? t.category?.name ?? "")
          .toString()
          .toLowerCase();
        if (selectedCategory === "Safety Checklist") {
          return cat.includes("checklist") || cat.includes("inspection");
        } else if (selectedCategory === "Observation") {
          return cat.includes("observation");
        }
        return true;
      });
    }

    if (!searchQuery.trim()) return result;
    const q = searchQuery.trim().toLowerCase();
    return result.filter((t) => {
      const title = (t.title ?? t.name ?? "").toString().toLowerCase();
      const formatNo = (
        getFormatNo(t.report_header_meta) ??
        t.template_code ??
        t.id ??
        ""
      )
        .toString()
        .toLowerCase();
      const category = (t.category_name ?? "").toString().toLowerCase();
      const projName = projectMap.get(t.project_id) ?? "";
      const status = (t.status ?? "").toString().toLowerCase();
      return (
        title.includes(q) ||
        formatNo.includes(q) ||
        category.includes(q) ||
        String(projName).toLowerCase().includes(q) ||
        status.includes(q)
      );
    });
  }, [templates, searchQuery, projectMap, selectedCategory]);

  // Selectable owner templates
  const selectableOwnerTemplates = useMemo(() => {
    return filteredTemplates.filter((t) => t.is_owner !== false);
  }, [filteredTemplates]);

  const handleToggleSelect = (templateId, isOwner) => {
    if (!isOwner) return;
    const next = new Set(selectedTemplateIds);
    if (next.has(templateId)) {
      next.delete(templateId);
    } else {
      next.add(templateId);
    }
    setSelectedTemplateIds(next);
  };

  const handleSelectAll = () => {
    if (selectedTemplateIds.size === selectableOwnerTemplates.length && selectableOwnerTemplates.length > 0) {
      setSelectedTemplateIds(new Set());
    } else {
      const next = new Set();
      selectableOwnerTemplates.forEach((t) => next.add(t.id));
      setSelectedTemplateIds(next);
    }
  };

  const handleOpenAssignmentModal = (template) => {
    setAssignmentTargetTemplate(template);
    setAssignmentTargetTemplates([]);
    setAssignmentModalOpen(true);
  };

  const handleOpenBulkAssignmentModal = () => {
    const selected = filteredTemplates.filter((t) =>
      selectedTemplateIds.has(t.id)
    );
    if (selected.length === 0) {
      showToast("Please select at least one owner template to assign.", "error");
      return;
    }
    setAssignmentTargetTemplate(null);
    setAssignmentTargetTemplates(selected);
    setAssignmentModalOpen(true);
  };

  const handleCreate = () => {
    navigate("/safetySetup/create");
  };

  const openEditTemplate = (template) => {
    const id = template.id ?? template.pk;

    if (!id) {
      showToast("Template id not found.", "error");
      return;
    }

    navigate(`/safetySetup/create?editTemplateId=${id}`);
  };

  const handleView = (e, template) => {
    e?.stopPropagation?.();
    const id = template.id ?? template.pk;
    if (id == null) return;
    setViewOpen(true);
    setViewLoading(true);
    getSafetyTemplate(id)
      .then((res) => {
        const data = res?.data ?? res;
        setViewingTemplate(data);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to load template.";
        showToast(msg, "error");
        setViewOpen(false);
      })
      .finally(() => setViewLoading(false));
  };

  const handleDelete = async (e, template) => {
    e?.stopPropagation?.();
    const id = template.id ?? template.pk;
    if (id == null) return;
    if (!window.confirm("Are you sure you want to delete this template?"))
      return;
    setDeletingId(id);
    try {
      await deleteSafetyTemplate(id);
      showToast("Template deleted successfully.", "success");
      fetchTemplates();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to delete template.";
      showToast(msg, "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-content-bg p-6">
      <div className="bg-card rounded-2xl p-6 min-h-[calc(100vh-3rem)] shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-semibold text-foreground">
            Safety Inspection Checklists
          </h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Create, manage, and assign Safety Checklists across projects.
        </p>

        {/* Toolbar & Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by title, format no..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-border bg-background pl-10 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            />
          </div>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent/10 hover:border-primary/40 transition-colors"
          >
            <SlidersHorizontal className="h-4 w-4 text-primary" />
            Filter
          </button>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="All">All Categories</option>
            <option value="Safety Checklist">Safety Checklist</option>
            <option value="Observation">Observation</option>
          </select>

          {/* Bulk Action Bar */}
          {selectedTemplateIds.size > 0 && (
            <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-lg animate-in fade-in">
              <span className="text-xs font-semibold text-primary">
                {selectedTemplateIds.size} template(s) selected
              </span>
              <button
                type="button"
                onClick={handleOpenBulkAssignmentModal}
                className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow hover:brightness-105 transition-all"
              >
                <UsersRound className="h-3.5 w-3.5" />
                Assign to Projects
              </button>
              <button
                type="button"
                onClick={() => setSelectedTemplateIds(new Set())}
                className="text-xs text-muted-foreground hover:text-foreground px-1.5 py-1"
              >
                Clear
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsProjectReportConfigModalOpen(true)}
            className="flex items-center gap-2 rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-medium text-foreground hover:bg-accent/10 hover:border-primary/40 transition-colors shadow-sm"
            title="Configure Inspection Report project abbreviations"
          >
            <SlidersHorizontal className="h-4 w-4 text-primary" />
            Project Report Settings
          </button>

          <div className="flex-1" />
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-md hover:shadow-lg hover:brightness-105 active:scale-[0.98] transition-all"
          >
            <Plus className="h-4 w-4" />
            Create
          </button>
        </div>

        {/* Table */}
        <div className="w-full overflow-x-auto">
          <div
            className="grid gap-x-6 gap-y-0 text-xs font-semibold uppercase tracking-wider text-primary"
            style={{ gridTemplateColumns, minWidth: "min(100%, 860px)" }}
          >
            {COLUMNS.map((col) => {
              if (col.id === "select") {
                const allSelected =
                  selectableOwnerTemplates.length > 0 &&
                  selectedTemplateIds.size === selectableOwnerTemplates.length;
                return (
                  <div
                    key={col.id}
                    className="px-2 py-3.5 border-b border-border flex items-center justify-center"
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                      title="Select all owner templates"
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
                    />
                  </div>
                );
              }
              return (
                <div
                  key={col.id}
                  className="px-3 py-3.5 border-b border-border text-center"
                >
                  {col.label}
                </div>
              );
            })}
          </div>

          <div className="flex flex-col">
            {loading && (
              <div
                className="py-12 text-center text-muted-foreground"
                style={{ minWidth: "min(100%, 860px)" }}
              >
                Loading…
              </div>
            )}
            {!loading && error && (
              <div
                className="py-8 text-center"
                style={{ minWidth: "min(100%, 860px)" }}
              >
                <p className="text-destructive mb-2">{error}</p>
                <button
                  type="button"
                  onClick={fetchTemplates}
                  className="text-sm text-primary hover:underline"
                >
                  Retry
                </button>
              </div>
            )}
            {!loading && !error && filteredTemplates.length === 0 && (
              <div
                className="py-12 text-center text-muted-foreground"
                style={{ minWidth: "min(100%, 860px)" }}
              >
                No formats found. Click &quot;Create&quot; to add one.
              </div>
            )}
            {!loading &&
              !error &&
              filteredTemplates.length > 0 &&
              filteredTemplates.map((template, index) => {
                const isOwner = template.is_owner !== false;
                const isSelected = selectedTemplateIds.has(template.id);
                const projectName =
                  projectMap?.get(template.project_id) ??
                  template.project_name ??
                  `Project #${template.project_id}`;

                return (
                  <div
                    key={template.id ?? template.pk ?? index}
                    className={`grid gap-x-6 gap-y-0 px-3 py-3.5 text-sm text-foreground border-b border-border last:border-b-0 hover:bg-accent/5 transition-colors items-center ${
                      isSelected ? "bg-primary/5" : ""
                    }`}
                    style={{ gridTemplateColumns, minWidth: "min(100%, 860px)" }}
                  >
                    {COLUMNS.map((col) => {
                      if (col.id === "select") {
                        return (
                          <div
                            key={col.id}
                            className="flex items-center justify-center"
                          >
                            <input
                              type="checkbox"
                              disabled={!isOwner}
                              checked={isSelected}
                              onChange={() =>
                                handleToggleSelect(template.id, isOwner)
                              }
                              title={
                                isOwner
                                  ? "Select for bulk assignment"
                                  : "Shared template cannot be bulk selected"
                              }
                              className="h-4 w-4 rounded border-border text-primary focus:ring-primary/30 disabled:opacity-30 disabled:cursor-not-allowed"
                            />
                          </div>
                        );
                      }

                      if (col.id === "project") {
                        return (
                          <div
                            key={col.id}
                            className="flex flex-col items-center justify-center gap-1 text-center"
                          >
                            <span className="font-medium text-xs text-foreground truncate max-w-[130px]" title={projectName}>
                              {projectName}
                            </span>
                            <div className="flex flex-wrap items-center justify-center gap-1">
                              {isOwner ? (
                                <>
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                    Owner
                                  </span>
                                  {template.assigned_projects_count > 0 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAssignmentModal(template);
                                      }}
                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors"
                                      title="Click to view/manage assigned projects"
                                    >
                                      Shared ({template.assigned_projects_count})
                                    </button>
                                  )}
                                </>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20"
                                  title={`Shared from source project #${template.project_id}`}
                                >
                                  <Globe className="h-2.5 w-2.5" /> Shared
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      }

                      if (col.id === "actions") {
                        return (
                          <div
                            key={col.id}
                            className="flex items-center justify-center gap-2"
                          >
                            {isOwner ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openEditTemplate(template)}
                                  disabled={!template.is_latest}
                                  title="Edit Template (Creates New Version)"
                                  className="rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800 p-1.5 text-orange-700 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/40 disabled:opacity-50 transition-colors"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleOpenAssignmentModal(template)}
                                  title="Assign to Projects"
                                  className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-1.5 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                >
                                  <UsersRound className="h-4 w-4" />
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => handleView(e, template)}
                                  title="Preview Template"
                                  className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => handleDelete(e, template)}
                                  disabled={
                                    deletingId === (template.id ?? template.pk)
                                  }
                                  title="Delete Template"
                                  className="p-1.5 text-destructive hover:bg-destructive/10 rounded-lg disabled:opacity-50 transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  disabled
                                  title="Shared master template. Only source project can edit."
                                  className="rounded-lg border border-border bg-muted/40 p-1.5 text-muted-foreground opacity-40 cursor-not-allowed"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>

                                <button
                                  type="button"
                                  disabled
                                  title="Shared template. Only source project can manage sharing."
                                  className="rounded-lg border border-border bg-muted/40 p-1.5 text-muted-foreground opacity-40 cursor-not-allowed"
                                >
                                  <UsersRound className="h-4 w-4" />
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => handleView(e, template)}
                                  title="Preview Template"
                                  className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>

                                <button
                                  type="button"
                                  disabled
                                  title="Shared master template. Deletion restricted to source project."
                                  className="p-1.5 text-muted-foreground opacity-40 cursor-not-allowed"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        );
                      }

                      const value = getCellValue(
                        template,
                        col,
                        index,
                        projectMap,
                      );
                      return (
                        <div
                          key={col.id}
                          className="px-0 break-words text-center"
                          title={typeof value === "string" ? value : undefined}
                        >
                          {value}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Project Assignment Modal */}
      <SafetyProjectAssignmentModal
        isOpen={assignmentModalOpen}
        onClose={() => {
          setAssignmentModalOpen(false);
          setAssignmentTargetTemplate(null);
          setAssignmentTargetTemplates([]);
        }}
        template={assignmentTargetTemplate}
        templates={assignmentTargetTemplates}
        projects={projects}
        orgId={orgId}
        onSuccess={() => {
          fetchTemplates();
          setSelectedTemplateIds(new Set());
        }}
      />

      {/* Project Report Settings Modal */}
      <SafetyProjectReportConfigModal
        isOpen={isProjectReportConfigModalOpen}
        onClose={() => setIsProjectReportConfigModalOpen(false)}
        initialProjectId={projects?.[0]?.id}
        orgId={orgId}
      />

      {/* Preview Modal */}
      {viewOpen && (
        <div className="fixed inset-0 z-[70] bg-black/45">
          <div className="h-full w-full overflow-auto pt-16 pb-4 px-3 md:px-6 lg:pl-72">
            <div className="mx-auto w-full max-w-6xl rounded-2xl bg-white shadow-2xl border border-gray-200">
              <div className="sticky top-0 z-10 flex justify-end border-b border-gray-100 bg-white/95 p-3">
                <button
                  type="button"
                  onClick={() => {
                    setViewOpen(false);
                    setViewingTemplate(null);
                  }}
                  className="rounded-full bg-gray-100 p-2 text-gray-700 hover:bg-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-3 md:p-5">
                {viewLoading ? (
                  <div className="rounded-xl bg-white p-8 text-center text-muted-foreground">
                    Loading template preview...
                  </div>
                ) : viewingTemplate ? (
                  (() => {
                    if (viewingTemplate?.report_layout?.is_horizontal) {
                      return (
                        <SafetyReportTemplateHorizontal
                          schema={{
                            type:
                              viewingTemplate?.report_layout
                                ?.horizontal_format || "HORIZONTAL",
                            headers: (viewingTemplate?.questions || []).map(
                              (q) => ({
                                ...q,
                                options: Array.isArray(q.options)
                                  ? q.options.map((opt) => {
                                      if (opt == null) return "";
                                      if (typeof opt === "object") {
                                        return (
                                          opt.label ||
                                          opt.name ||
                                          opt.value ||
                                          ""
                                        );
                                      }
                                      return String(opt);
                                    })
                                  : typeof q.options === "string"
                                    ? [q.options]
                                    : [],
                              }),
                            ),
                          }}
                          reportTitle={
                            viewingTemplate?.title || viewingTemplate?.name
                          }
                          initialTemplateData={viewingTemplate}
                          previewOnly={true}
                        />
                      );
                    }

                    const catName =
                      viewingTemplate?.category_name ||
                      viewingTemplate?.category?.name ||
                      "";
                    const isObservation =
                      catName.toLowerCase() ===
                      "safety & housekeeping observation";

                    if (isObservation) {
                      return (
                        <ObservationReportTemplate
                          initialTemplateData={viewingTemplate}
                          previewOnly
                          selectedQuestions={[]}
                        />
                      );
                    }

                    return (
                      <SafetyReportTemplate
                        initialTemplateData={viewingTemplate}
                        previewOnly
                        selectedQuestions={[]}
                      />
                    );
                  })()
                ) : (
                  <div className="rounded-xl bg-white p-8 text-center text-muted-foreground">
                    No template data available.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SafetyFormats;
